import classNames from 'classnames';
import { Checkbox } from 'components/1-atoms/Checkbox';
import { DateRangePicker, DateRangePickerProps } from 'components/2-molecules/Inputs';
import { useEffect, useState } from 'react';
import { formatDate } from 'utilities/date';
import { SearchBox, SearchBoxProps } from '../SearchBox';
import { SelectedFilter, TableDropdown } from '../Table';
import styles from './ListFilters.module.scss';

// TODO Add date range input

export interface ListFiltersProps {
	className?: string;
	filters?: {
		groupId?: string;
		label: string;
		options: string[];
	}[];
	onShowHistoricChange?: (v: boolean) => void;
	historicCheckboxLabel?: string;
	removeFilter: string;
	choosenDropdownFilters?: (filters: { groupId: string; value: string }[]) => void;
	searchWord?: SearchBoxProps['searchWord'];
	dateRange?: DateRangePickerProps['callBack'];
	texts?: {
		searchActionAriaLabel?: string;
		searchPlaceholder?: string;
		nextMonth?: string;
		previousMonth?: string;
		dateRangePlaceholder?: string;
	};
}

export type ChoosenFilter = { groupId: string; value: string };

export const ListFilters: React.FC<ListFiltersProps> = ({
	className,
	filters,
	onShowHistoricChange,
	historicCheckboxLabel,
	removeFilter,
	texts,
	choosenDropdownFilters = null,
	searchWord,
	dateRange,
}) => {
	const [selectedTags, setSelectedTags] = useState<ChoosenFilter[]>([]);
	const [currentSearchWord, setCurrentSearchWord] = useState<string>(null);
	const [currentDates, setCurrentDates] = useState<string>(null);
	const [rawStartDate, setRawStartDate] = useState<Date>(null);
	const [rawEndDate, setRawEndDate] = useState<Date>(null);
	const [searchQuery, setSearchQuery] = useState('');

	const { searchActionAriaLabel, searchPlaceholder, nextMonth, previousMonth, dateRangePlaceholder } = texts ?? {};

	const filterOnSelect = (filter: ChoosenFilter) => {
		if (selectedTags?.find((tag) => tag?.value === filter.value)) return;
		setSelectedTags([...selectedTags, filter]);
	};

	const filterOnRemove = (filter: ChoosenFilter) => {
		const reducedFilters = selectedTags.filter((item) => {
			if (item.value === filter.value && item.groupId === filter.groupId) return false;
			return item;
		});
		setSelectedTags(reducedFilters);
	};

	useEffect(() => {
		choosenDropdownFilters && choosenDropdownFilters(selectedTags);
	}, [choosenDropdownFilters, selectedTags]);

	const handleSearch = (word: string) => {
		setCurrentSearchWord(word);
		searchWord && searchWord(word);
	};

	const handleDates = (dates: Date[]) => {
		const [start, end] = dates ?? [];
		setRawStartDate(start);
		if (start !== rawStartDate) setRawEndDate(null);
		setRawEndDate(end);

		if (!dates || !start || !end) {
			setCurrentDates(null);
			dateRange && dateRange(null);
			return;
		}

		const startDate = start ? formatDate(String(start), 'dd-mm-yyyy') : '';
		const endDate = end ? formatDate(String(end), 'dd-mm-yyyy') : '';
		setCurrentDates(`${startDate} - ${endDate}`);
		dateRange && dateRange(dates);
	};

	return (
		<div className={classNames(styles.ListFilters, className)}>
			{filters && filters.length > 0 && (
				<div className={styles.ListFilters_wrapper}>
					<div className={styles.ListFilters_inputs}>
						{filters.map((input, i) =>
							input.label && input?.options?.length > 0 ? (
								<div key={i} className={styles.ListFilters_input}>
									<TableDropdown
										title={input.label}
										id={`filter_${i}`}
										options={input.options}
										onSelect={(e) => {
											filterOnSelect({ groupId: input.groupId, value: e });
										}}
										disabled={input?.options?.length === 0}
									/>
								</div>
							) : null,
						)}
						{dateRange && (
							<div className={styles.ListFilters_dateRange}>
								<DateRangePicker
									ariaLabels={{ nextBtnAriaLabel: nextMonth, prevBtnAriaLabel: previousMonth }}
									id="listfilter"
									placeholder={dateRangePlaceholder}
									enableMonthSelect={true}
									enableYearSelect={true}
									yearOptions={{
										startYear: 1996,
										endYear: new Date().getFullYear(),
									}}
									startDate={rawStartDate}
									endDate={rawEndDate}
									callBack={handleDates}
								/>
							</div>
						)}
						{searchWord && (
							<div className={styles.ListFilters_search}>
								<SearchBox
									id="searchbox"
									searchActionAriaLabel={searchActionAriaLabel}
									searchPlaceholder={searchPlaceholder}
									query={searchQuery}
									searchWord={handleSearch}
									handleChangeCallback={(queryCallback) => setSearchQuery(queryCallback)}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			{selectedTags?.map((tagItem, i) => {
				return (
					<SelectedFilter
						key={i}
						className={styles.ListFilters_tag}
						id={`selectedDropDownFilter${i}`}
						text={tagItem?.value}
						removeFilterText={removeFilter}
						onElementRemoved={() => {
							filterOnRemove(tagItem);
						}}
					/>
				);
			})}
			{currentSearchWord?.length > 0 && currentSearchWord != '*' && (
				<SelectedFilter
					className={styles.ListFilters_tag}
					id={`selectedFilterSearch`}
					text={currentSearchWord != '*' && currentSearchWord}
					removeFilterText={removeFilter}
					onElementRemoved={() => {
						handleSearch('*');
						setSearchQuery('');
					}}
				/>
			)}
			{currentDates && (
				<SelectedFilter
					className={styles.ListFilters_tag}
					id={`selectedFilterDate`}
					text={currentDates}
					removeFilterText={removeFilter}
					onElementRemoved={() => {
						handleDates(null);
					}}
				/>
			)}
			{onShowHistoricChange && historicCheckboxLabel && (
				<Checkbox id={'historicCheckbox'} label={historicCheckboxLabel} onChange={(e) => onShowHistoricChange(e.target.checked)}></Checkbox>
			)}
		</div>
	);
};
